import { ConfigRegistry, ParamDef, CategoryDef } from '../engine/ConfigRegistry';

/**
 * Settings panel UI that builds from ConfigRegistry metadata.
 * Renders as a slide-in panel from the right with accordion categories.
 * When rootCategory is provided, only shows categories under that root.
 */
export class SettingsPanel {
  private registry: ConfigRegistry;
  private panel: HTMLElement;
  private overlay: HTMLElement;
  private openCategory: string | null = null;
  private isMobile = false;
  private visible = false;
  private isDebug = false;
  private rootCategory: string | undefined;

  constructor(rootCategory?: string) {
    this.rootCategory = rootCategory;
    this.registry = ConfigRegistry.getInstance();
    this.isMobile = window.innerWidth <= 600 || window.matchMedia('(pointer: coarse)').matches;
    this.isDebug = window.location.search.includes('debug');

    const suffix = rootCategory ? `-${rootCategory}` : '';

    // Create overlay backdrop
    this.overlay = document.createElement('div');
    this.overlay.id = `settings-overlay${suffix}`;
    this.overlay.addEventListener('click', () => this.hide());
    document.body.appendChild(this.overlay);

    // Create panel
    this.panel = document.createElement('div');
    this.panel.id = `settings-panel${suffix}`;
    document.body.appendChild(this.panel);
  }

  /**
   * Build the panel content from registry metadata.
   * Call this after all params are registered.
   */
  build(): void {
    this.panel.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.className = 'settings-header';
    const titleText = this.rootCategory
      ? this.registry.getAllCategories().find(c => c.key === this.rootCategory)?.label ?? 'Settings'
      : 'Settings';
    header.innerHTML = `
      <span class="settings-title">${titleText} Settings</span>
      <div class="settings-header-btns">
        <button class="settings-reset-all" title="Reset all to defaults">Reset All</button>
        <button class="settings-close" title="Close">&times;</button>
      </div>
    `;
    header.querySelector('.settings-close')!.addEventListener('click', () => this.hide());
    header.querySelector('.settings-reset-all')!.addEventListener('click', () => {
      if (this.rootCategory) {
        this.registry.resetAllUnder(this.rootCategory);
      } else {
        this.registry.resetAll();
      }
      this.rebuildInputs();
    });
    this.panel.appendChild(header);

    // Scrollable content area
    const content = document.createElement('div');
    content.className = 'settings-content';
    this.panel.appendChild(content);

    // Build accordion from categories
    const categories = this.registry.getAllCategories();

    // Group by top-level parent, filtered to rootCategory if set
    const topLevel = categories.filter(c => !c.parent);
    const filteredTopLevel = this.rootCategory
      ? topLevel.filter(c => c.key === this.rootCategory)
      : topLevel;

    for (const tl of filteredTopLevel) {
      const group = document.createElement('div');
      group.className = 'settings-group';

      // Only show group header if we're showing multiple roots
      if (!this.rootCategory) {
        const groupHeader = document.createElement('div');
        groupHeader.className = 'settings-group-header';
        groupHeader.textContent = tl.label;
        group.appendChild(groupHeader);
      }

      // Sub-categories (skip debug-only when not in debug mode)
      const subCats = categories.filter(c => c.parent === tl.key && (!c.debugOnly || this.isDebug));
      for (const cat of subCats) {
        group.appendChild(this.buildCategory(cat));
      }

      // Also include params directly in top-level category
      const directParams = this.registry.getCategory(tl.key);
      if (directParams.length > 0) {
        group.appendChild(this.buildCategory(tl));
      }

      content.appendChild(group);
    }

    // Categories without parent (orphans) - only when unfiltered
    if (!this.rootCategory) {
      const orphans = categories.filter(c => c.parent && !categories.some(p => p.key === c.parent));
      for (const cat of orphans) {
        content.appendChild(this.buildCategory(cat));
      }
    }
  }

  private buildCategory(cat: CategoryDef): HTMLElement {
    const section = document.createElement('div');
    section.className = 'settings-section';
    section.dataset.category = cat.key;

    // Section header (accordion toggle)
    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'settings-section-header';

    const hasChanges = this.registry.categoryHasChanges(cat.key);
    sectionHeader.innerHTML = `
      <span class="settings-section-arrow">&#9654;</span>
      <span class="settings-section-label">${cat.label}</span>
      ${hasChanges ? '<span class="settings-modified-dot"></span>' : ''}
    `;

    sectionHeader.addEventListener('click', () => {
      const isOpen = this.openCategory === cat.key;
      this.openCategory = isOpen ? null : cat.key;
      this.updateAccordionState();
    });

    section.appendChild(sectionHeader);

    // Section body (params)
    const body = document.createElement('div');
    body.className = 'settings-section-body';

    const params = this.registry.getCategory(cat.key);
    for (const param of params) {
      body.appendChild(this.buildParamRow(param));
    }

    // Reset button for category
    if (params.length > 0) {
      const resetBtn = document.createElement('button');
      resetBtn.className = 'settings-reset-cat';
      resetBtn.textContent = 'Reset to Defaults';
      resetBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.registry.resetCategory(cat.key);
        this.rebuildInputs();
      });
      body.appendChild(resetBtn);
    }

    section.appendChild(body);
    return section;
  }

  /**
   * Whether this param displays in seconds (duration with default >= 1000ms).
   */
  private isSecondsDisplay(param: ParamDef): boolean {
    return param.type === 'duration' && param.defaultValue >= 1000;
  }

  /**
   * Convert internal value to display value.
   * Duration (>=1000ms default): ms → seconds
   * Ratio: 0-1 → 0-100%
   */
  private toDisplay(value: number, param: ParamDef): number {
    if (this.isSecondsDisplay(param)) return value / 1000;
    if (param.type === 'ratio') return value * 100;
    return value;
  }

  /**
   * Convert display value back to internal value.
   */
  private fromDisplay(displayValue: number, param: ParamDef): number {
    if (this.isSecondsDisplay(param)) return displayValue * 1000;
    if (param.type === 'ratio') return displayValue / 100;
    return displayValue;
  }

  /**
   * Get the display unit string for a param.
   */
  private getDisplayUnit(param: ParamDef): string {
    if (this.isSecondsDisplay(param)) {
      // Convert ms-based units: "ms" → "s", "ms/cell" → "s/cell"
      return (param.unit || 'ms').replace('ms', 's');
    }
    if (param.type === 'ratio') return '%';
    return param.unit || '';
  }

  private buildParamRow(param: ParamDef): HTMLElement {
    const row = document.createElement('div');
    row.className = 'settings-param-row';
    row.dataset.key = param.key;

    const currentValue = this.registry.get(param.key);
    const isDefault = currentValue === param.defaultValue;

    // Label row (label + help button)
    const labelRow = document.createElement('div');
    labelRow.className = 'settings-param-label-row';

    const label = document.createElement('span');
    label.className = 'settings-param-label';
    let labelText = param.label;
    if (param.restartRequired) {
      labelText += ' <span class="settings-restart-icon" title="Takes effect on restart">&#9888;</span>';
    }
    label.innerHTML = labelText;
    if (param.description) {
      label.title = param.description;
    }
    labelRow.appendChild(label);

    // Help button
    if (param.helpText) {
      const helpBtn = document.createElement('button');
      helpBtn.className = 'settings-help-btn';
      helpBtn.textContent = '?';
      helpBtn.title = 'Show detailed help';
      helpBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.showHelpDialog(param);
      });
      labelRow.appendChild(helpBtn);
    }

    row.appendChild(labelRow);

    // Controls container
    const controls = document.createElement('div');
    controls.className = 'settings-param-controls';

    if (param.enumLabels && param.enumLabels.length > 0) {
      // Enum-style: render as <select> dropdown
      const select = document.createElement('select');
      select.className = 'settings-enum-select';
      for (let i = 0; i < param.enumLabels.length; i++) {
        const opt = document.createElement('option');
        opt.value = String(i);
        opt.textContent = param.enumLabels[i];
        select.appendChild(opt);
      }
      select.value = String(Math.round(currentValue));
      controls.appendChild(select);

      select.addEventListener('change', () => {
        const val = parseInt(select.value, 10);
        this.registry.set(param.key, val);
        this.updateModifiedDots();
      });
    } else {
      // Standard slider + number input
      const slider = document.createElement('input');
      slider.type = 'range';
      slider.className = 'settings-slider';
      slider.min = String(param.min);
      slider.max = String(param.max);
      slider.step = String(param.step);
      slider.value = String(currentValue);
      controls.appendChild(slider);

      // Number input (operates in display units)
      const numInput = document.createElement('input');
      numInput.type = 'number';
      numInput.className = 'settings-num-input';
      numInput.min = String(this.toDisplay(param.min, param));
      numInput.max = String(this.toDisplay(param.max, param));
      numInput.step = String(this.toDisplay(param.step, param));
      numInput.value = this.formatDisplayValue(this.toDisplay(currentValue, param), param);
      controls.appendChild(numInput);

      // Unit label
      const displayUnit = this.getDisplayUnit(param);
      if (displayUnit) {
        const unitEl = document.createElement('span');
        unitEl.className = 'settings-unit';
        unitEl.textContent = displayUnit;
        controls.appendChild(unitEl);
      }

      // Sync slider → numInput (slider is internal, numInput is display)
      slider.addEventListener('input', () => {
        const internalVal = parseFloat(slider.value);
        numInput.value = this.formatDisplayValue(this.toDisplay(internalVal, param), param);
        this.registry.set(param.key, internalVal);
        this.updateModifiedDots();
      });

      // Sync numInput → slider (numInput is display, convert back to internal)
      numInput.addEventListener('change', () => {
        let displayVal = parseFloat(numInput.value);
        const displayMin = this.toDisplay(param.min, param);
        const displayMax = this.toDisplay(param.max, param);
        if (isNaN(displayVal)) displayVal = this.toDisplay(param.defaultValue, param);
        displayVal = Math.max(displayMin, Math.min(displayMax, displayVal));
        const internalVal = this.fromDisplay(displayVal, param);
        slider.value = String(internalVal);
        numInput.value = this.formatDisplayValue(displayVal, param);
        this.registry.set(param.key, internalVal);
        this.updateModifiedDots();
      });
    }

    row.appendChild(controls);

    // Modified indicator
    if (!isDefault) {
      row.classList.add('modified');
    }

    return row;
  }

  /**
   * Format a display-unit value for the number input.
   */
  private formatDisplayValue(displayValue: number, param: ParamDef): string {
    if (param.type === 'integer') return String(Math.round(displayValue));
    if (param.type === 'duration') {
      if (this.isSecondsDisplay(param)) return displayValue.toFixed(1);
      return String(Math.round(displayValue));
    }
    if (param.type === 'ratio') return displayValue.toFixed(0);

    // Float - match step precision
    const str = param.step.toString();
    const dot = str.indexOf('.');
    const precision = dot >= 0 ? str.length - dot - 1 : 0;
    return displayValue.toFixed(precision);
  }

  private updateAccordionState(): void {
    const sections = this.panel.querySelectorAll('.settings-section');
    for (const section of sections) {
      const el = section as HTMLElement;
      const cat = el.dataset.category;
      const isOpen = cat === this.openCategory;
      el.classList.toggle('open', isOpen);
      const arrow = el.querySelector('.settings-section-arrow');
      if (arrow) arrow.textContent = isOpen ? '\u25BC' : '\u25B6';
    }
  }

  private updateModifiedDots(): void {
    const sections = this.panel.querySelectorAll('.settings-section');
    for (const section of sections) {
      const el = section as HTMLElement;
      const cat = el.dataset.category;
      if (!cat) continue;
      const hasChanges = this.registry.categoryHasChanges(cat);
      const dot = el.querySelector('.settings-modified-dot');
      const header = el.querySelector('.settings-section-header');
      if (hasChanges && !dot && header) {
        const newDot = document.createElement('span');
        newDot.className = 'settings-modified-dot';
        header.appendChild(newDot);
      } else if (!hasChanges && dot) {
        dot.remove();
      }
    }

    // Update param rows
    const rows = this.panel.querySelectorAll('.settings-param-row');
    for (const row of rows) {
      const el = row as HTMLElement;
      const key = el.dataset.key;
      if (!key) continue;
      const param = this.registry.getAllParams().find(p => p.key === key);
      if (!param) continue;
      const current = this.registry.get(key);
      el.classList.toggle('modified', current !== param.defaultValue);
    }

    // Update reset all button state
    const resetAll = this.panel.querySelector('.settings-reset-all') as HTMLButtonElement;
    if (resetAll) {
      if (this.rootCategory) {
        resetAll.disabled = !this.registry.hasAnyChangesUnder(this.rootCategory);
      } else {
        resetAll.disabled = !this.registry.hasAnyChanges();
      }
    }
  }

  /**
   * Rebuild all input values from registry (after reset).
   */
  private rebuildInputs(): void {
    const rows = this.panel.querySelectorAll('.settings-param-row');
    for (const row of rows) {
      const el = row as HTMLElement;
      const key = el.dataset.key;
      if (!key) continue;
      const param = this.registry.getAllParams().find(p => p.key === key);
      if (!param) continue;
      const value = this.registry.get(key);

      const enumSelect = el.querySelector('.settings-enum-select') as HTMLSelectElement;
      if (enumSelect) {
        enumSelect.value = String(Math.round(value));
      } else {
        const slider = el.querySelector('.settings-slider') as HTMLInputElement;
        const numInput = el.querySelector('.settings-num-input') as HTMLInputElement;
        if (slider) slider.value = String(value);
        if (numInput) numInput.value = this.formatDisplayValue(this.toDisplay(value, param), param);
      }
    }
    this.updateModifiedDots();
  }

  /**
   * Show a help dialog for a parameter with detailed description and related settings.
   */
  private showHelpDialog(param: ParamDef): void {
    // Remove existing dialog if any
    this.hideHelpDialog();

    const dialog = document.createElement('div');
    dialog.className = 'settings-help-dialog';

    // Header
    const header = document.createElement('div');
    header.className = 'settings-help-dialog-header';
    const title = document.createElement('span');
    title.className = 'settings-help-dialog-title';
    title.textContent = param.label;
    header.appendChild(title);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'settings-help-dialog-close';
    closeBtn.innerHTML = '&times;';
    closeBtn.addEventListener('click', () => this.hideHelpDialog());
    header.appendChild(closeBtn);
    dialog.appendChild(header);

    // Body
    const body = document.createElement('div');
    body.className = 'settings-help-dialog-body';

    // Help text
    const text = document.createElement('div');
    text.className = 'settings-help-dialog-text';
    text.textContent = param.helpText!;
    body.appendChild(text);

    // Details table
    const details = document.createElement('div');
    details.className = 'settings-help-dialog-details';
    const displayUnit = this.getDisplayUnit(param);
    const defVal = this.formatDisplayValue(this.toDisplay(param.defaultValue, param), param);
    const minVal = this.formatDisplayValue(this.toDisplay(param.min, param), param);
    const maxVal = this.formatDisplayValue(this.toDisplay(param.max, param), param);
    const unitSuffix = displayUnit ? ` ${displayUnit}` : '';
    details.innerHTML = `
      <div class="settings-help-detail"><span class="settings-help-detail-label">Default:</span> <span class="settings-help-detail-value">${defVal}${unitSuffix}</span></div>
      <div class="settings-help-detail"><span class="settings-help-detail-label">Range:</span> <span class="settings-help-detail-value">${minVal} – ${maxVal}${unitSuffix}</span></div>
      <div class="settings-help-detail"><span class="settings-help-detail-label">Type:</span> <span class="settings-help-detail-value">${param.type}</span></div>
    `;
    body.appendChild(details);

    // Related settings
    if (param.relatedSettings && param.relatedSettings.length > 0) {
      const relatedSection = document.createElement('div');
      relatedSection.className = 'settings-help-dialog-related';
      const relatedTitle = document.createElement('div');
      relatedTitle.className = 'settings-help-related-title';
      relatedTitle.textContent = 'Related Settings';
      relatedSection.appendChild(relatedTitle);

      for (const relKey of param.relatedSettings) {
        const relParam = this.registry.getAllParams().find(p => p.key === relKey);
        if (!relParam) continue;
        const relLink = document.createElement('button');
        relLink.className = 'settings-help-related-link';
        relLink.textContent = relParam.label;
        if (relParam.description) {
          relLink.title = relParam.description;
        }
        relLink.addEventListener('click', () => {
          this.hideHelpDialog();
          this.scrollToParam(relKey);
        });
        relatedSection.appendChild(relLink);
      }
      body.appendChild(relatedSection);
    }

    dialog.appendChild(body);

    // Backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'settings-help-dialog-backdrop';
    backdrop.addEventListener('click', () => this.hideHelpDialog());
    this.panel.appendChild(backdrop);
    this.panel.appendChild(dialog);
  }

  private hideHelpDialog(): void {
    const existing = this.panel.querySelector('.settings-help-dialog');
    if (existing) existing.remove();
    const backdrop = this.panel.querySelector('.settings-help-dialog-backdrop');
    if (backdrop) backdrop.remove();
  }

  /**
   * Scroll to and briefly highlight a parameter row.
   */
  private scrollToParam(key: string): void {
    // Find the category for this param
    const param = this.registry.getAllParams().find(p => p.key === key);
    if (!param) return;

    // Open the category accordion
    this.openCategory = param.category;
    this.updateAccordionState();

    // Find and scroll to the row
    setTimeout(() => {
      const row = this.panel.querySelector(`.settings-param-row[data-key="${key}"]`) as HTMLElement;
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        row.classList.add('settings-highlight');
        setTimeout(() => row.classList.remove('settings-highlight'), 1500);
      }
    }, 50);
  }

  show(): void {
    this.visible = true;
    this.rebuildInputs();
    this.panel.classList.add('open');
    this.overlay.classList.add('open');
  }

  hide(): void {
    this.visible = false;
    this.panel.classList.remove('open');
    this.overlay.classList.remove('open');
  }

  toggle(): void {
    if (this.visible) this.hide();
    else this.show();
  }

  isVisible(): boolean {
    return this.visible;
  }
}
